import React from 'react';
import i18next from 'i18next';
import {type List} from 'immutable';
import {type InjectedFormProps} from 'redux-form';
import styled from 'styled-components';

import {compose} from 'web-app/util/typescript';
import {reduxForm} from 'web-app/react/deprecation/redux-form/redux-form';
import {connectState} from 'web-app/util/connect-state';
import {Base, Flex} from 'web-app/react/components/layout/layout';
import {s2, s4, s8, s10, getSpacing} from 'web-app/styleguide/spacing';
import Link from 'web-app/react/components/link/link';
import {media} from 'web-app/styleguide/utils';
import PlatformHelper from 'web-app/helpers/platform-helper';
import {Title, Subheader, Body} from 'web-app/react/components/text/text';
import Button, {ButtonAppearance, ButtonType} from 'web-app/react/components/form-elements/button/button';
import {Colors} from 'web-app/styleguide';
import LightningBolt from 'web-app/react/components/icons/legacy/lazy-icons/lightning-bolt';
import {FormField, FormSelect} from 'web-app/react/components/form-elements/containers';
import APIHelper from 'web-app/services/api-helper';
import {navigationContext} from 'web-app/react/contexts/navigation';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import {isNonEmpty} from 'web-app/util/non-empty-array';
import RouteConfig from 'web-app/react/routes/route-config';
import {useDebounce} from 'web-app/react/hooks/use-debounce';
import useTypedSelector from 'web-app/react/hooks/use-typed-selector';

import {type LoginFormData, type ExistingLogin, type LoginSelectorOption, type LoginError, fieldNames} from './models';
import {SubmitButton, LoginFormTextField, LoginButtonStyle} from './shared-components';
import * as Actions from './actions';
import * as Selectors from './selectors';
import * as Helpers from './helpers';
import * as Queries from './queries';
import {
    type RequestConnectTokenEmailMutation,
    type RequestConnectTokenEmailMutationVariables,
} from './__generated__/queries.api-types';

const StyledMagicLinkCopyContainer = styled(Flex)`
    text-align: center;
`;

const StyledLightningBolt = styled(LightningBolt)`
    position: absolute;
    left: 12px;
    top: 12px;
`;

const StyledSubmitButton = styled(SubmitButton)`
    position: relative;
`;

const OpenIdButton = styled(Button)`
    ${LoginButtonStyle};
    margin-bottom: ${getSpacing(s4)};
`;

interface InputProps {
    onSubmit: (data: LoginFormData) => void;
    email?: string;
    errorState?: LoginError;
    showMagicLinkOption: boolean;
    magicLinkSent: boolean;
    locale?: string;
}

type OpenIdProviders = ReturnType<typeof Selectors.openIdProviders>;
type InferListType<C> = C extends List<infer P> ? P : never;
type OpenIdProvider = InferListType<OpenIdProviders>;

interface StateProps {
    fetchingExistingLogins: boolean;
    loading: boolean;
    disableLoginForm: boolean;
    formEmail?: string;
    initialEmail?: string;
    errorState?: LoginError;
    openIdProviders: OpenIdProviders;
    cannotLoginWithPassword: ReturnType<typeof Selectors.cannotLoginWithPassword>;
    requestOpenIdConnectTokenInfo: ReturnType<typeof Selectors.requestOpenIdConnectTokenInfo>;
}

type ReduxFormProps = InjectedFormProps<LoginFormData, InputProps & StateProps>;

type Props = InputProps & StateProps & ReduxFormProps;

const LoginForm: React.FC<React.PropsWithChildren<Props>> = React.memo(props => {
    const {
        openIdProviders,
        cannotLoginWithPassword,
        change,
        errorState,
        formEmail,
        requestOpenIdConnectTokenInfo,
        locale,
        initialized,
        initialValues,
    } = props;

    const {generateUrl, transitionTo} = React.useContext(navigationContext);

    const checkForExistingLogin = React.useCallback((email: string | undefined | null) => {
        if (Helpers.isValidEmail(email)) {
            Actions.testEmailForLogins.dispatch(email);
        } else {
            Actions.resetEmailTest.dispatch();
        }
    }, []);

    // When the login form is initalized with an email,
    // we want start with asking the /exists endpoint for info about the email.
    React.useEffect(() => {
        if (initialized) {
            /**
             * `checkForExistingLogin` has a side-effect when called with no value, as it "resets" the form.
             * Otherwise the "email" input field and the buttons / dropdowns that are shown based on the result from /exists might get out of sync.
             */
            checkForExistingLogin(initialValues?.email);
        }
    }, [initialized, initialValues, checkForExistingLogin]);

    const debouncedEmailCheck = useDebounce(checkForExistingLogin, []);

    const handleEmailChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (errorState) {
                Actions.resetError.dispatch();
            }
            change('loginTLD', undefined);
            debouncedEmailCheck(e.target.value);
        },
        [errorState, change, debouncedEmailCheck],
    );

    const backToLoginButtonHandler = React.useCallback(() => {
        Actions.clearMagicLinkSent.dispatch();
    }, []);

    const handleOpenIdButtonClick = React.useCallback(
        (openIdProvider: OpenIdProvider) => {
            if (!formEmail) {
                return;
            }

            const state = {
                route: 'openid.redirect',
                openIDProviderId: openIdProvider.openIDProviderId,
                email: formEmail,
            };

            const stateKey = Helpers.saveOpenIdState(state);
            const redirectUrl = Helpers.buildOpenIdRedirectUrl();
            const openIdUrl = Helpers.buildOpenIdConnectAuthenticationUrl(
                openIdProvider.url,
                openIdProvider.params,
                redirectUrl,
                stateKey,
            );

            Helpers.handleOpenIdLogin(openIdUrl, queryParamsFromRedirect => {
                // On the mobile app, we cannot redirect to the other server setup.
                // So we must point to the correct servers "manually"
                if (PlatformHelper.isMobileApp()) {
                    APIHelper.setApiConfig(openIdProvider.selectedApiKey);
                }

                transitionTo(state.route, {
                    queryParams: {
                        ...queryParamsFromRedirect,
                        openIDProviderId: state.openIDProviderId,
                        email: state.email,
                    },
                });
            });
        },
        [formEmail, transitionTo],
    );

    const [isRequestingConnectToken, setIsRequestingConnectToken] = React.useState(false);

    const handleRequestConnectToken = React.useCallback(
        (apiKey: string) => {
            if (!formEmail || !isNonEmpty(requestOpenIdConnectTokenInfo)) {
                return;
            }

            setIsRequestingConnectToken(true);

            // On the mobile app, we cannot redirect to the other server setup.
            // So we must point to the correct servers "manually"
            getApolloClient(PlatformHelper.isMobileApp() ? apiKey : undefined)
                .mutate<RequestConnectTokenEmailMutation, RequestConnectTokenEmailMutationVariables>({
                    mutation: Queries.RequestConnectTokenEmail,
                    variables: {
                        email: formEmail,
                    },
                })
                .then(result => {
                    transitionTo(RouteConfig.connectTokenSent.emberIdent, {
                        queryParams: {
                            email: result.data?.me.requestOpenIdConnectToken ?? formEmail,
                            locale,
                        },
                    });
                })
                .finally(() => {
                    setIsRequestingConnectToken(false);
                });
        },
        [formEmail, requestOpenIdConnectTokenInfo, locale, transitionTo],
    );

    const canResetPassword = useTypedSelector(state => Selectors.canResetPassword(state));

    return (
        <form onSubmit={props.handleSubmit}>
            {!props.magicLinkSent ? (
                <FormField
                    component={LoginFormTextField}
                    name={fieldNames('email')}
                    type="email"
                    id="email"
                    placeholder={i18next.t('email')}
                    onChange={handleEmailChange}
                    required
                    autoFocus={!PlatformHelper.isMobileApp() && !props.initialEmail}
                />
            ) : null}
            <FormField
                component={LoginFormTextField}
                name={fieldNames('password')}
                type="password"
                id="password"
                placeholder={i18next.t('password')}
                onChange={Actions.resetError.dispatch}
                disabled={props.disableLoginForm}
                required={!(props.showMagicLinkOption || cannotLoginWithPassword)}
                autoFocus={!PlatformHelper.isMobileApp() && Boolean(props.initialEmail)}
                hidden={props.showMagicLinkOption || cannotLoginWithPassword}
            />
            {props.showMagicLinkOption ? (
                <StyledMagicLinkCopyContainer paddingTop={s10} paddingBottom={s8} direction="column">
                    <Subheader
                        emphasized
                        marginBottom={s2}
                        data-e2e-id={props.magicLinkSent ? 'MagicLinkSentCaption' : 'MagicLinkCaption'}
                    >
                        {props.magicLinkSent
                            ? i18next.t('login.magicLinkSentCaption')
                            : i18next.t('login.magicLinkCaption')}
                    </Subheader>
                    <Body secondary>
                        {props.magicLinkSent
                            ? i18next.t('login.magicLinkSentDescription', {email: props.formEmail})
                            : i18next.t('login.magicLinkDescription')}
                    </Body>
                </StyledMagicLinkCopyContainer>
            ) : null}
            {!props.showMagicLinkOption ? (
                <div>
                    <LoginAmbiguity />
                </div>
            ) : null}
            {!props.showMagicLinkOption ? (
                <StyledFlex justify="flex-end" align="center">
                    {canResetPassword ? (
                        <Link
                            id="forgotPassword"
                            href={generateUrl('forgotPassword', {
                                queryParams: {email: props.formEmail},
                            })}
                        >
                            {i18next.t('login.forgotPassword')}
                        </Link>
                    ) : null}
                </StyledFlex>
            ) : null}
            {openIdProviders
                ? openIdProviders.map(openIdProvider => {
                      return (
                          <OpenIdButton
                              key={openIdProvider.openIDProviderId}
                              type={ButtonType.link}
                              appearance={cannotLoginWithPassword ? ButtonAppearance.confirm : ButtonAppearance.default}
                              onClick={() => handleOpenIdButtonClick(openIdProvider)}
                          >
                              <Subheader
                                  emphasized
                                  color={cannotLoginWithPassword ? Colors.InvertedText : Colors.Text}
                                  ellipsis
                              >
                                  {i18next.t('login.loginWith', {provider: openIdProvider.title})}
                              </Subheader>
                          </OpenIdButton>
                      );
                  })
                : null}
            {(props.showMagicLinkOption && !props.magicLinkSent) || !cannotLoginWithPassword ? (
                <StyledSubmitButton
                    id="loginSubmit"
                    type="submit"
                    disabled={props.disableLoginForm || undefined}
                    spinner={props.fetchingExistingLogins || props.loading}
                >
                    {!props.showMagicLinkOption ? (
                        i18next.t('login.login')
                    ) : (
                        <Base data-e2e-id="magic-link-login-button-content">
                            <StyledLightningBolt background={Colors.InvertedText} size={24} />
                            <Flex align="center">
                                <Subheader emphasized color={Colors.InvertedText}>
                                    {i18next.t('login.magicLinkButton')}
                                </Subheader>
                            </Flex>
                        </Base>
                    )}
                </StyledSubmitButton>
            ) : null}
            {props.magicLinkSent ? (
                <Flex marginTop={s8} justify="center">
                    <Button
                        id="backToLogin"
                        type={ButtonType.link}
                        href={generateUrl('login')}
                        onClick={backToLoginButtonHandler}
                    >
                        {i18next.t('login.backToLogin')}
                    </Button>
                </Flex>
            ) : null}
            {isNonEmpty(requestOpenIdConnectTokenInfo)
                ? requestOpenIdConnectTokenInfo.map(info => {
                      return (
                          <Flex marginTop={s8} justify="center">
                              <StyledSubmitButton
                                  onClick={() => handleRequestConnectToken(info.selectedApiKey)}
                                  spinner={isRequestingConnectToken}
                              >
                                  {i18next.t('login.requestConnectToken')}
                              </StyledSubmitButton>
                          </Flex>
                      );
                  })
                : null}
        </form>
    );
});

export default compose(
    connectState<InputProps, StateProps, React.ComponentType<InputProps & StateProps>>((state, props) => ({
        fetchingExistingLogins: state.login.fetchingExistingLogins,
        loading: state.login.loading,
        disableLoginForm: Selectors.disableLoginForm(state),
        initialValues: Selectors.initialLoginFormValues(state, props),
        initialEmail: Selectors.initialLoginFormEmailValue(state, props),
        formEmail: Selectors.loginEmail(state),
        errorState: state.login.errorState,
        openIdProviders: Selectors.openIdProviders(state),
        cannotLoginWithPassword: Selectors.cannotLoginWithPassword(state),
        requestOpenIdConnectTokenInfo: Selectors.requestOpenIdConnectTokenInfo(state),
    })),
)
    .with(
        reduxForm<LoginFormData, InputProps & StateProps>({
            form: 'LOGIN_FORM',
            enableReinitialize: true,
        }),
    )
    .apply(LoginForm);

interface LoginAmbiguityStateProps {
    lastCheckedEmail?: string;
    showLoginOptions: boolean;
    loginSelectorOptions?: LoginSelectorOption[];
    externalLogins: List<ExistingLogin>;
    email?: string;
}
type LoginAmbiguityProps = LoginAmbiguityStateProps;

class BaseLoginAmbiguity extends React.Component<LoginAmbiguityProps> {
    constructor(props) {
        super(props);
        this.handleSelectLogin = this.handleSelectLogin.bind(this);
    }
    public render() {
        if (
            !this.props.showLoginOptions ||
            !this.props.lastCheckedEmail ||
            this.props.lastCheckedEmail !== this.props.email
        ) {
            return null;
        }

        if (this.props.loginSelectorOptions) {
            return (
                <Base marginTop={s4}>
                    <FormField
                        component={StyledSelect}
                        name={fieldNames('loginTLD')}
                        options={this.props.loginSelectorOptions}
                        clearable={false}
                        required
                        props={{
                            onChange: this.handleSelectLogin,
                        }}
                    />
                </Base>
            );
        }

        return (
            <>
                {this.props.externalLogins.map(login => (
                    <Base marginTop={s2} marginBottom={s2} key={login.baseUrl}>
                        <Button
                            type="link"
                            href={`https://app.${login.baseUrl}/#/login?email=${this.props.lastCheckedEmail}`}
                            position="fill"
                        >
                            <Title emphasized>
                                {i18next.t('login.goToUrl', {
                                    url: login.baseUrl,
                                })}
                            </Title>
                        </Button>
                    </Base>
                ))}
            </>
        );
    }

    private handleSelectLogin(option: LoginSelectorOption | null) {
        if (option && !PlatformHelper.isMobileApp() && !option.local) {
            window.location.href = `https://app.${option.value}/#/login?email=${this.props.lastCheckedEmail}`;
        }
    }
}

const LoginAmbiguity = compose(
    connectState<{}, LoginAmbiguityStateProps, React.ComponentType<{} & LoginAmbiguityStateProps>>(state => ({
        lastCheckedEmail: state.login.lastCheckedEmail,
        showLoginOptions: Selectors.showLoginOptions(state),
        loginSelectorOptions: Selectors.loginSelectorOptions(state),
        externalLogins: Selectors.externalLogins(state),
        email: Selectors.loginEmail(state),
    })),
).apply(BaseLoginAmbiguity);

const StyledSelect = styled(FormSelect)`
    && {
        &.Select--single > .Select-control .Select-value,
        .Select-placeholder {
            line-height: 46px;
        }
        .Select-input {
            height: 46px;
            line-height: 46px;
        }

        margin-bottom: 8px;
    }
`;

const StyledFlex = styled(Flex)`
    margin-top: 16px;
    margin-bottom: 48px;
    ${media.mobileSideways`
        margin-bottom: 16px;
    `}
`;
